import { type NodeTypes } from '@xyflow/react';

import {
  ArtifactNode,
  ClauseExtractionNode,
  ConstantNode,
  DatasetNode,
  EmbeddingsNode,
  FileNode,
  IdentityNode,
  InputNode,
  JoinNode,
  JsonTemplateNode,
  LegalVarianceDeterminationNode,
  LLMNode,
  MapNode,
  OutputNode,
  PDFReaderNode,
  SemanticFilterNode,
  StringTemplateNode,
  TextSplitterNode,
  TransformNode,
  ZipNode,
} from '../components/nodes';

/**
 * Node type classification based on block type
 */
export const getReactFlowNodeType = (blockType: string): string => {
  // Each block type now maps to its own specific node component with custom_ prefix
  return `custom_${blockType}`;
};

/**
 * Get the category for a given block type
 */
export const getBlockCategory = (blockType: string): string => {
  // IO Blocks
  if (
    ['constant', 'input', 'output', 'dataset', 'file', 'artifact'].includes(
      blockType,
    )
  ) {
    return 'io';
  }

  // Flow Blocks
  if (['identity', 'zip', 'map', 'join'].includes(blockType)) {
    return 'flow';
  }

  // AI Blocks (processing category)
  if (['llm', 'embeddings', 'semantic_filter'].includes(blockType)) {
    return 'processing';
  }

  // Molecule Blocks (processing category)
  if (['clause_extraction_agent', 'legal_variance_determination'].includes(blockType)) {
    return 'processing';
  }

  // Transform Blocks
  if (
    [
      'string_template',
      'json_template',
      'text_splitter',
      'transform',
      'pdf_reader',
    ].includes(blockType)
  ) {
    return 'transform';
  }

  // Default to flow if unknown
  return 'flow';
};

/**
 * React Flow node types registry
 */
export const nodeTypes: NodeTypes = {
  // IO Blocks
  custom_input: InputNode,
  custom_output: OutputNode,
  custom_constant: ConstantNode,
  custom_dataset: DatasetNode,
  custom_file: FileNode,
  custom_artifact: ArtifactNode,

  // Flow Blocks
  custom_identity: IdentityNode,
  custom_zip: ZipNode,
  custom_map: MapNode,
  custom_join: JoinNode,

  // AI Blocks
  custom_llm: LLMNode,
  custom_embeddings: EmbeddingsNode,
  custom_semantic_filter: SemanticFilterNode,

  // Molecule Blocks
  custom_clause_extraction_agent: ClauseExtractionNode,
  custom_legal_variance_determination: LegalVarianceDeterminationNode,

  // Transform Blocks
  custom_string_template: StringTemplateNode,
  custom_json_template: JsonTemplateNode,
  custom_text_splitter: TextSplitterNode,
  custom_transform: TransformNode,
  custom_pdf_reader: PDFReaderNode,
};

/**
 * Get all supported block types
 */
export const getSupportedBlockTypes = (): string[] => [
  // IO Blocks
  'constant',
  'input',
  'output',
  'dataset',
  'file',
  'artifact',

  // Flow Blocks
  'identity',
  'zip',
  'map',
  'join',

  // AI Blocks
  'llm',
  'embeddings',
  'semantic_filter',

  // Molecule Blocks
  'clause_extraction_agent',
  'legal_variance_determination',

  // Transform Blocks
  'string_template',
  'json_template',
  'text_splitter',
  'transform',
  'pdf_reader',
];
